import { useMemo } from "react";
import { Filter, X, Search } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const ALL = "__all__";
const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

// Distinct values with counts, case-insensitive, keeping first-seen casing. Sorted by count desc then name.
export const countBy = (contacts, field) => {
  const m = new Map();
  for (const c of contacts) {
    const raw = (c[field] || "").trim();
    if (!raw) continue;
    const k = raw.toLowerCase();
    const e = m.get(k) || { name: raw, n: 0 };
    e.n += 1; m.set(k, e);
  }
  return [...m.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
};

export const matchesFilters = (c, { position, company }) =>
  (!position || (c.title || "").trim().toLowerCase() === position.toLowerCase()) &&
  (!company || (c.company || "").trim().toLowerCase() === company.toLowerCase());

export const ContactsFilterButton = ({ contacts, filters, onChange, search, onSearch }) => {
  const positions = useMemo(() => countBy(contacts, "title"), [contacts]);
  const companies = useMemo(() => countBy(contacts, "company"), [contacts]);
  const active = [filters.position, filters.company].filter(Boolean).length;
  const set = (k, v) => onChange({ ...filters, [k]: v === ALL ? "" : v });
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant={active ? "default" : "outline"} data-testid="contacts-filter-btn">
          <Filter className="w-4 h-4 mr-1" /> Filter{active ? ` (${active})` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 z-[60]" data-testid="contacts-filter-popover">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Type to search the list…" className="pl-8 h-9" autoComplete="off" data-testid="contacts-filter-search" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Position</Label>
          <Select value={filters.position || ALL} onValueChange={(v) => set("position", v)}>
            <SelectTrigger data-testid="filter-position-select"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[70]">
              <SelectItem value={ALL}>All positions</SelectItem>
              {positions.map((p) => <SelectItem key={p.name} value={p.name} data-testid={`filter-position-${slug(p.name)}`}>{p.name} ({p.n})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Company</Label>
          <Select value={filters.company || ALL} onValueChange={(v) => set("company", v)}>
            <SelectTrigger data-testid="filter-company-select"><SelectValue /></SelectTrigger>
            <SelectContent className="z-[70]">
              <SelectItem value={ALL}>All companies</SelectItem>
              {companies.map((p) => <SelectItem key={p.name} value={p.name} data-testid={`filter-company-${slug(p.name)}`}>{p.name} ({p.n})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(active > 0 || search) && (
          <Button type="button" variant="ghost" size="sm" className="w-full" onClick={() => { onChange({ position: "", company: "" }); onSearch(""); }} data-testid="contacts-filter-clear">
            <X className="w-4 h-4 mr-1" /> Clear filters
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
